import styled from "@emotion/styled";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { ANIMATION_SPEEDS, colors, spacing } from "../../design-tokens";
import { ActionButton } from "../../primitives/action-button";
import { ActionsBar, ActionsGroup } from "../../primitives/actions-bar";
import { AnimatedSection } from "../../primitives/animated-section";
import { CommentOptions } from "./comment-options";
import { FreeTextInput } from "./free-text-input";
import { FEEDBACK_NEGATIVE_VALUE, FEEDBACK_POSITIVE_VALUE, useUserFeedback } from "./use-user-feedback";

const StyledAnimatedSection = styled(AnimatedSection)<{ isVisible: boolean }>`
  margin-top: ${props => props.isVisible ? spacing.xl : 0}px;
  transition: margin-top ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out;
`;

const FeedbackLabel = styled.p`
  color: ${colors.text};
  font-size: 14px;
  font-weight: 500;
  text-wrap: nowrap;
  margin: 0;
  width: 100%;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background-color: ${colors.border};
  transition: background-color 200ms ease-in-out;
  
  &:hover {
    background-color: ${colors.subtleBorder};
  }
`;

const FeedbackButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs}px;
`;

type UserFeedbackComponentProps = {
  readonly traceId?: string;
  readonly observationId?: string;
  readonly additionalAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
};

export function UserFeedbackComponent({ traceId, observationId, additionalAction }: UserFeedbackComponentProps) {
  const {
    feedback,
    isSubmitting,
    selectedComment,
    freeTextComment,
    shouldShowComments,
    shouldShowTextArea,
    isPositive,
    isVisible,
    handleUserFeedback,
    handleCommentSelection,
    handleFreeTextSubmit,
    handleBackToComments,
    setFreeTextComment,
  } = useUserFeedback({ traceId, observationId });

  return (
    <AnimatedSection
      isVisible={isVisible}
      animationType="fadeSlide"
    >
      <ActionsBar>
        <ActionsGroup>
          <FeedbackLabel>Did we answer your question?</FeedbackLabel>
          <FeedbackButtonGroup>
            <ActionButton
              variant="secondary"
              size="small"
              isActive={feedback === FEEDBACK_POSITIVE_VALUE}
              onClick={() => handleUserFeedback(FEEDBACK_POSITIVE_VALUE)}
              aria-label="Mark as helpful"
              title="Helpful"
              disabled={isSubmitting}
            >
              <ThumbsUpIcon strokeWidth={2} size={16} />
            </ActionButton>
            <ActionButton
              variant="secondary"
              size="small"
              isActive={feedback === FEEDBACK_NEGATIVE_VALUE}
              onClick={() => handleUserFeedback(FEEDBACK_NEGATIVE_VALUE)}
              aria-label="Mark as not helpful"
              title="Not helpful"
              disabled={isSubmitting}
            >
              <ThumbsDownIcon strokeWidth={2} size={16} />
            </ActionButton>
          </FeedbackButtonGroup>
        </ActionsGroup>
        {additionalAction && (
          <>
            <Divider />
            <ActionButton
              variant="secondary"
              size="small"
              onClick={additionalAction.onClick}
              aria-label={additionalAction.label}
              title={additionalAction.label}
            >
              {additionalAction.icon}
            </ActionButton>
          </>
        )}
      </ActionsBar>

      <StyledAnimatedSection
        isVisible={shouldShowComments}
        animationType="fadeSlide"
      >
        <CommentOptions
          selectedComment={selectedComment}
          isSubmitting={isSubmitting}
          isPositive={isPositive}
          onCommentSelect={handleCommentSelection}
        />
      </StyledAnimatedSection>

      <StyledAnimatedSection
        isVisible={shouldShowTextArea}
        animationType="fadeSlide"
      >
        <FreeTextInput
          isSubmitting={isSubmitting}
          onBack={handleBackToComments}
          onChange={setFreeTextComment}
          onSubmit={handleFreeTextSubmit}
          showBack={isPositive}
          value={freeTextComment}
        />
      </StyledAnimatedSection>
    </AnimatedSection>
  );
}
